/**
 * Sélecteur d'entreprise d'un compte personnel (décision du propriétaire du 25
 * octobre 2026, modèle Pennylane, Slack, Google) : là où l'entreprise et le rôle
 * s'affichent déjà — le Menu au téléphone, le menu du compte sur ordinateur —
 * la liste des entreprises du compte, l'actuelle repérée, chacune avec son rôle
 * et les envois qui y attendent. Un appui passe à une autre ; hors ligne, le
 * refus est dit sans rien changer.
 *
 * Rien n'apparaît pour un compte d'une seule entreprise, ni en assistance.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../core/auth.js';
import { roleDisplayName, t } from '../core/i18n.js';
import { membershipsOf, type Membership } from '../core/memberships.js';
import { ICONS } from '../design/icons.js';
import { waitingWorkFor } from '../offline/sync.js';
import { RecordIcon, RecordList, RecordRow } from './RecordRow.js';
import { Badge, BadgeGroup } from './ui/Badge.js';
import { Banner } from './ui/Feedback.js';
import { Section } from './ui/Section.js';

/** Fiche du comptable (`AccountantProfilePage`), page de compte. */
const ACCOUNTANT_PATH = '/compte/comptable';

export interface CompanySwitch {
  /** Entreprises du compte ; vide s'il n'y en a qu'une (rien à choisir). */
  companies: Membership[];
  /** Envois en attente par adhésion, lus dans la base de chacune sur cet appareil. */
  waiting: Record<string, number>;
  /** Adhésion vers laquelle la bascule est en cours. */
  pending: string | undefined;
  error: string | undefined;
  choose: (userId: string) => Promise<void>;
}

/** Libellé d'une entreprise du compte : son rôle, ou pourquoi elle n'est pas ouvrable. */
export function companyLine(company: Membership): string {
  if (company.advisorHold) return t('compteEntreprises.comptableAttenteLigne');
  if (company.status !== 'actif') {
    const key = `compteEntreprises.indisponible.${company.status}`;
    const label = t(key);
    return label === key ? t('compteEntreprises.indisponible.inactif') : label;
  }
  return roleDisplayName({ role: company.role, company_role_name: company.roleName });
}

export function useCompanySwitch(watch = true): CompanySwitch {
  const { me, switchCompany } = useAuth();
  const all = me && !me.support_session ? membershipsOf(me) : [];
  const companies = all.length > 1 ? all : [];
  const [waiting, setWaiting] = useState<Record<string, number>>({});
  const [pending, setPending] = useState<string>();
  const [error, setError] = useState<string>();
  const ids = companies.map((company) => company.userId).join(',');

  useEffect(() => {
    if (!watch || !ids) return undefined;
    let alive = true;
    void Promise.all(ids.split(',').map(async (userId) => [userId, await waitingWorkFor(userId).catch(() => 0)] as const))
      .then((entries) => {
        if (alive) setWaiting(Object.fromEntries(entries));
      });
    return () => { alive = false; };
  }, [ids, watch]);

  const choose = async (userId: string): Promise<void> => {
    setError(undefined);
    setPending(userId);
    try {
      await switchCompany(userId);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('commun.erreurInattendue'));
    } finally {
      setPending(undefined);
    }
  };
  return { companies, waiting, pending, error, choose };
}

/** Pastilles d'une entreprise : actuelle, bascule en cours, envois qui y attendent. */
function CompanyBadges(props: { company: Membership; waiting: number; pending: boolean }): ReactNode {
  const badges = [
    props.company.current ? <Badge key="actuelle" tone="ok">{t('compteEntreprises.actuelle')}</Badge> : null,
    props.pending ? <Badge key="bascule" tone="info">{t('compteEntreprises.enCours')}</Badge> : null,
    props.waiting > 0
      ? <Badge key="envois" tone="warn" title={t('compteEntreprises.envoisAide')}>{t('compteEntreprises.envois', { n: props.waiting })}</Badge>
      : null,
  ].filter(Boolean);
  if (badges.length === 0) return null;
  return badges.length === 1 ? badges[0] : <BadgeGroup>{badges}</BadgeGroup>;
}

/**
 * Les entreprises du compte en liste (Menu du téléphone, écran d'entreprise
 * suspendue) : une ligne par entreprise, un appui pour y passer.
 */
export function CompanySection(): ReactNode {
  const { companies, waiting, pending, error, choose } = useCompanySwitch();
  const navigate = useNavigate();
  if (companies.length === 0) return null;
  return (
    <Section title={t('compteEntreprises.titre')} hint={t('compteEntreprises.aide')} flush>
      {error ? <Banner tone="danger">{error}</Banner> : null}
      <RecordList label={t('compteEntreprises.titre')}>
        {companies.map((company) => {
          const open = company.status === 'actif' && !company.current && !pending;
          // Place de comptable en attente : la fiche du comptable, page du compte, l'ouvrira.
          const onOpen = company.advisorHold ? () => navigate(ACCOUNTANT_PATH)
            : open ? () => void choose(company.userId) : undefined;
          return (
            <li key={company.userId}>
              <RecordRow
                className="company-switch__row"
                {...(onOpen ? { onOpen } : {})}
                lead={<RecordIcon><ICONS.entreprise size={18} aria-hidden="true" /></RecordIcon>}
                title={company.companyName}
                lines={[companyLine(company)]}
                badge={<CompanyBadges company={company} waiting={waiting[company.userId] ?? 0} pending={pending === company.userId} />}
              />
            </li>
          );
        })}
      </RecordList>
    </Section>
  );
}
